/* The fund ledger — ONE read every screen counts from (Ale ruling
 * 2026-09-04, Tier-1 decision 2 "one ledger, read everywhere").
 *
 * Served by GET /api/funds/{ref}/ledger (one fund) and GET /api/funds/ledger
 * (every visible fund, one sweep). The types mirror nolos/funds/schemas.py;
 * the WORDS below are the only place a ledger figure is put into words, so
 * Today, the Funds list, the Workbench tiles and chips, the Record banner,
 * the chain-status sentence, the Evidence header and the two queue headers
 * print the same figure with the same word.
 *
 * Pure (no imports) so the bare node runner can prove the words — see
 * tests/unit/ledgerWords.test.mjs. */

export type SinceKind = 'last_completed_session' | 'fallback_24h' | 'query'

export interface LedgerBreakItem {
  task_id: string
  task_type: string
  status: string
  target_type: string | null
  target_id: string | null
  instrument_id: string | null
  instrument_name: string | null
  /** The engine's own delta (record minus what the movements say). */
  delta: string | null
}

export interface LedgerBreaks {
  /** Breaks still standing — snoozed cards INCLUDED (a snooze parks the
   *  card, never the break). */
  open: number
  /** Of those, parked cards. */
  snoozed: number
  items: LedgerBreakItem[]
}

export interface LedgerOwed {
  demanded: number
  proven: number
  pending_signoff: number
  /** demanded − proven − pending sign-off: THE owed figure. */
  total: number
  /** Of `total`, rows whose obligation is not mandatory. */
  optional: number
  /** Of `total`, rows anchored to a plug trade — never chased until the
   *  break is approved. */
  on_plugs: number
}

export interface LedgerChangedSince {
  since: string
  since_kind: SinceKind
  count: number
  last_document: { name: string; date: string } | null
}

export interface FundLedger {
  fund_id: string
  fund_name: string
  slug: string | null
  as_at: string
  to_book: number
  to_decide: number
  to_walk: number
  breaks: LedgerBreaks
  owed: LedgerOwed
  awaiting_signoff: number
  changed_since: LedgerChangedSince
  total_open: number
}

export interface LedgerUnattributed {
  to_book: number
  to_decide: number
}

export interface FundLedgerList {
  as_at: string
  since: string
  since_kind: SinceKind
  funds: FundLedger[]
  unattributed: LedgerUnattributed
}

/** The figures of several ledgers added up — a queue tile over every
 *  fund, a queue header with no fund filter. */
export interface LedgerTotals {
  to_book: number
  to_decide: number
  to_walk: number
  breaks: LedgerBreaks
  owed: LedgerOwed
  awaiting_signoff: number
  total_open: number
}

export function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/** "5 owed · 3 optional · 2 on plugs" — the split clauses only when they
 *  carry a figure; "nothing owed" at zero. */
export function owedWords(o: LedgerOwed): string {
  if (o.total <= 0) return 'nothing owed'
  const parts = [`${o.total} owed`]
  if (o.optional > 0) parts.push(`${o.optional} optional`)
  if (o.on_plugs > 0) parts.push(`${o.on_plugs} on ${o.on_plugs === 1 ? 'a plug' : 'plugs'}`)
  return parts.join(' · ')
}

/** "2 breaks (1 snoozed)" — the parked card counted, and said. */
export function breaksWords(b: LedgerBreaks): string {
  if (b.open <= 0) return 'no breaks'
  const base = plural(b.open, 'break', 'breaks')
  return b.snoozed > 0 ? `${base} (${b.snoozed} snoozed)` : base
}

/** "34 of 39 proven". */
export function provenWords(o: LedgerOwed): string {
  return `${o.proven} of ${o.demanded} proven`
}

/** Distinct instrument names the standing breaks sit on, first-seen order. */
export function breakNames(b: LedgerBreaks): string[] {
  const out: string[] = []
  for (const i of b.items) {
    const n = i.instrument_name
    if (n && !out.includes(n)) out.push(n)
  }
  return out
}

/** The Record Keeping figure a tile or a row prints: what is to book,
 *  decide and walk, together. */
export function recordKeepingTotal(l: {
  to_book: number
  to_decide: number
  to_walk: number
}): number {
  return l.to_book + l.to_decide + l.to_walk
}

/** The five queues, by the key the workbench places work under. */
export type QueueKey =
  'record_keeping' | 'reconciliation' | 'ownership_verification' | 'chase_request' | 'other_ops'

/** The figure the FUND SELECTOR prints for a fund on THIS page: on a
 *  queue, that queue's own count; on Today, the Workbench or a fund page
 *  (`null`), every open item. Other ops keeps no ledger figure of its
 *  own — it carries the unroutable safety net — so it counts the fund's
 *  open work like the pages that name no queue. */
export function queueOpen(queue: QueueKey | null, l: FundLedger): number {
  switch (queue) {
    case 'record_keeping':
      return recordKeepingTotal(l)
    case 'reconciliation':
      return l.breaks.open
    case 'ownership_verification':
      return l.awaiting_signoff
    case 'chase_request':
      return l.owed.total
    default:
      return l.total_open
  }
}

/** The noun after the selector's figure — the queue's own word, so the
 *  count and the queue the officer is standing in agree. Record keeping
 *  keeps the plain word: its figure spans booking, deciding and looking
 *  through, so no one verb would be honest about it. */
export function queueNoun(queue: QueueKey | null): string {
  switch (queue) {
    case 'reconciliation':
      return 'to reconcile'
    case 'ownership_verification':
      return 'to verify'
    case 'chase_request':
      return 'owed'
    default:
      return 'open'
  }
}

/** What is open on a fund, in clauses — the sub-line under its name in
 *  the selector: "1 to decide · 2 breaks · 5 owed". Empty when the fund
 *  has nothing open (its row then stands in the muted group). */
export function openWords(l: FundLedger): string {
  const parts: string[] = []
  if (l.to_book > 0) parts.push(`${l.to_book} to book`)
  if (l.to_decide > 0) parts.push(`${l.to_decide} to decide`)
  if (l.to_walk > 0) parts.push(`${l.to_walk} to look through`)
  if (l.breaks.open > 0) parts.push(breaksWords(l.breaks))
  if (l.owed.total > 0) parts.push(`${l.owed.total} owed`)
  if (l.awaiting_signoff > 0) parts.push(`${l.awaiting_signoff} to verify`)
  return parts.join(' · ')
}

export function emptyTotals(): LedgerTotals {
  return {
    to_book: 0,
    to_decide: 0,
    to_walk: 0,
    breaks: { open: 0, snoozed: 0, items: [] },
    owed: { demanded: 0, proven: 0, pending_signoff: 0, total: 0, optional: 0, on_plugs: 0 },
    awaiting_signoff: 0,
    total_open: 0,
  }
}

/** Σ over ledgers; the fund-less deck-queue items (a cold-start founding
 *  decision) join the to-book / to-decide figures when given — they count
 *  on the tile, on no chip. */
export function sumLedgers(rows: FundLedger[], unattributed?: LedgerUnattributed): LedgerTotals {
  const t = emptyTotals()
  for (const r of rows) {
    t.to_book += r.to_book
    t.to_decide += r.to_decide
    t.to_walk += r.to_walk
    t.breaks.open += r.breaks.open
    t.breaks.snoozed += r.breaks.snoozed
    t.breaks.items.push(...r.breaks.items)
    t.owed.demanded += r.owed.demanded
    t.owed.proven += r.owed.proven
    t.owed.pending_signoff += r.owed.pending_signoff
    t.owed.total += r.owed.total
    t.owed.optional += r.owed.optional
    t.owed.on_plugs += r.owed.on_plugs
    t.awaiting_signoff += r.awaiting_signoff
  }
  if (unattributed) {
    t.to_book += unattributed.to_book
    t.to_decide += unattributed.to_decide
  }
  t.total_open =
    t.to_book + t.to_decide + t.to_walk + t.breaks.open + t.owed.total + t.awaiting_signoff
  return t
}

/** The record-side task shape the Record tab's RECO rule reads
 *  (recordDerive.taskDelta): a ledger break item as such a task, so a
 *  parked (snoozed) break still shows its delta on its row. */
export function breakItemAsTask(i: LedgerBreakItem): {
  task_id: string
  task_type: string
  target_id: string | null
  task_data: { diagnosis: { delta: string | null } }
} {
  return {
    task_id: i.task_id,
    task_type: i.task_type,
    target_id: i.target_id,
    task_data: { diagnosis: { delta: i.delta } },
  }
}
